import { useHistory } from 'react-router-dom';
import { Check } from 'lucide-react';

import { Routes } from '@/utils/routes';
import { cn } from '@/lib/utils';
import type { InvoiceRunsQuery } from './InvoiceRuns.generated';
import {
  formatCount,
  formatDateLong,
  formatDateTimeShort,
  isCharged,
  isEsStylePeriod,
  isGenerationRun,
  periodLabel,
  runPresentationState,
  runTypeLongLabel,
  shortRunId,
} from './invoiceRunPresentation';
import { DryRunPill, RunStatusLabel, RunTypePill } from './RunPills';

export type RunRow = InvoiceRunsQuery['invoiceRuns'][number];

const GRID_COLS =
  'tw:grid tw:grid-cols-[minmax(150px,1.2fr)_130px_minmax(170px,1fr)_148px_128px_78px_96px] tw:items-center tw:gap-x-3 tw:px-4';

function ProgressCell({ run }: { run: RunRow }) {
  const state = runPresentationState(run);
  const stalled = state === 'stalled';
  const processed = run.processed_count ?? 0;
  const total = run.total_count ?? 0;
  const pct = total > 0 ? Math.min(100, (processed / total) * 100) : 0;
  return (
    <div className="tw:flex tw:flex-col tw:gap-1 tw:py-1">
      <RunStatusLabel state={state} />
      <div className="tw:h-1 tw:w-full tw:overflow-hidden tw:rounded-full tw:bg-muted">
        <div
          className={cn(
            'tw:h-full tw:rounded-full',
            stalled ? 'tw:bg-amber-500' : 'tw:bg-sky-600',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="tw:text-xs tw:tabular-nums tw:text-muted-foreground">
        {formatCount(processed)} / {formatCount(total)} stores
      </span>
    </div>
  );
}

function ErrorsCell({ run }: { run: RunRow }) {
  const count = run.error_count;
  if (count == null) {
    return <span className="tw:text-sm tw:text-muted-foreground">—</span>;
  }
  const open = run.open_error_count;
  const resolved = count - open;
  return (
    <div className="tw:flex tw:flex-col">
      <span
        className={cn(
          'tw:text-sm tw:tabular-nums',
          count > 0 && open > 0
            ? 'tw:font-semibold tw:text-destructive'
            : 'tw:text-muted-foreground',
        )}
      >
        {formatCount(count)}
      </span>
      {count > 0 && resolved > 0 && (
        <span className="tw:text-xs tw:text-muted-foreground">
          {open === 0 ? 'all resolved' : `${formatCount(resolved)} resolved`}
        </span>
      )}
    </div>
  );
}

function ChargedCell({ run }: { run: RunRow }) {
  if (!isGenerationRun(run) || run.dry_run || run.finished_at == null) {
    return <span className="tw:text-sm tw:text-muted-foreground">—</span>;
  }
  if (isCharged(run)) {
    return (
      <span className="tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:text-emerald-600 tw:dark:text-emerald-400">
        <Check className="tw:size-3.5" />
        Charged
      </span>
    );
  }
  return <span className="tw:text-sm tw:text-muted-foreground">Not yet</span>;
}

export function RunHistoryTable({
  runs,
  nextScheduledRun,
}: {
  /** History rows — scheduled runs already filtered out by the caller. */
  runs: RunRow[];
  /** For the first-month empty state. */
  nextScheduledRun?: RunRow;
}) {
  const history = useHistory();

  return (
    <section className="tw:flex tw:flex-col tw:gap-3">
      <h2 className="tw:text-lg tw:font-semibold">Run history</h2>
      <div className="tw:overflow-x-auto tw:rounded-lg tw:border tw:border-border tw:bg-card">
        <div className="tw:min-w-[900px]">
          <div
            className={cn(
              GRID_COLS,
              'tw:h-10 tw:border-b tw:border-border tw:text-xs tw:font-medium tw:text-muted-foreground',
            )}
          >
            <span>Billing period</span>
            <span>Type</span>
            <span>Status</span>
            <span>Started / finished</span>
            <span>Triggered by</span>
            <span>Errors</span>
            <span>Charged</span>
          </div>
          {runs.length === 0 && (
            <div className="tw:px-4 tw:py-8 tw:text-sm tw:text-muted-foreground">
              {nextScheduledRun
                ? `No runs yet. The first ${runTypeLongLabel(
                    nextScheduledRun.type,
                  )} starts ${
                    nextScheduledRun.due_at
                      ? formatDateLong(nextScheduledRun.due_at)
                      : 'soon'
                  }, in the evening.`
                : 'No runs yet.'}
            </div>
          )}
          {runs.map((run) => {
            const state = runPresentationState(run);
            const isRunning = state === 'running' || state === 'stalled';
            return (
              <button
                key={run.id}
                type="button"
                onClick={() => history.push(Routes.monthlyInvoicingRun(run.id))}
                className={cn(
                  GRID_COLS,
                  'tw:w-full tw:cursor-pointer tw:border-b tw:border-border tw:py-2.5 tw:text-left tw:transition-colors tw:last:border-b-0 tw:hover:bg-accent/50',
                )}
              >
                <div className="tw:flex tw:flex-col">
                  <span className="tw:text-sm tw:font-medium">
                    {periodLabel(isEsStylePeriod(run), run.year, run.iso_month)}
                  </span>
                  <span className="tw:font-mono tw:text-xs tw:text-muted-foreground">
                    {shortRunId(run.id)}
                  </span>
                </div>
                <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1">
                  <RunTypePill type={run.type} />
                  {run.dry_run && <DryRunPill />}
                </div>
                {isRunning ? (
                  <ProgressCell run={run} />
                ) : (
                  <RunStatusLabel state={state} />
                )}
                <div className="tw:flex tw:flex-col">
                  <span className="tw:text-sm tw:tabular-nums">
                    {run.started_at ? formatDateTimeShort(run.started_at) : '—'}
                  </span>
                  <span className="tw:text-sm tw:tabular-nums tw:text-muted-foreground">
                    {run.finished_at
                      ? formatDateTimeShort(run.finished_at)
                      : ''}
                  </span>
                </div>
                <span className="tw:truncate tw:text-sm tw:text-muted-foreground">
                  {!run.requested_by || run.requested_by === 'schedule'
                    ? 'schedule'
                    : run.requested_by}
                </span>
                <ErrorsCell run={run} />
                <ChargedCell run={run} />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
